import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { AppRoutes, DashboardModules } from "../config/app.config";
import { Table, Thead, Trow, Tcol, Tbody } from "../Generic/Table";
import Button from "../Generic/Button";
import Loader from "../Generic/Loader";
import EmptyState from "../Generic/EmptyState";
import InlineRetry from "../Generic/InlineRetry";
import OfferStatusBadge from "./Components/shared/OfferStatusBadge";
import OffersFilterBar from "./Components/OffersFilterBar";
import { useOffersList } from "./hooks/useOffersList";
import { OFFERS_LOAD_MORE_TRIGGER_OFFSET, OFFERS_SEARCH_DEBOUNCE_MS } from "./constants";
import { normalizeOffersFilters } from "./utils/offersListFilters";
import { zIndex } from "../config/zIndex";

const Page = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	padding: 1.5rem;
	box-sizing: border-box;
`;

const ListRegion = styled.div`
	position: relative;
	flex: 1;
	min-height: 0;
`;

const ListScroll = styled.div`
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
`;

const LoadingOverlay = styled.div`
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(247, 247, 248, 0.72);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.loadingOverlay};
	pointer-events: all;
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin};
`;

function scrollToRequiredItem(id) {
	const element = document.getElementById(id);
	if (!element) return;
	element.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
}

function readFiltersFromSearchParams(searchParams) {
	return normalizeOffersFilters({
		query: searchParams.get("query"),
		restaurantId: searchParams.get("restaurantId"),
	});
}

/** Writes already-normalized filter values into the URL search params. */
function writeFiltersToSearchParams(searchParams, { query, restaurantId }) {
	const next = new URLSearchParams(searchParams);
	if (query) {
		next.set("query", query);
	} else {
		next.delete("query");
	}
	next.delete("q");
	if (restaurantId) {
		next.set("restaurantId", restaurantId);
	} else {
		next.delete("restaurantId");
	}
	return next;
}

function renderLoadMoreFooter({ loadingMore, loadMoreError, currentPage, onRetryLoadMore }) {
	if (loadingMore) {
		return (
			<CenteredContainer>
				<Loader />
			</CenteredContainer>
		);
	}
	if (loadMoreError) {
		return (
			<InlineRetry
				message="Failed to load more offers"
				retry={() => onRetryLoadMore(currentPage + 1)}
				disabled={loadingMore}
			/>
		);
	}
	return null;
}

function renderListContent({
	initialLoading,
	initialError,
	offers,
	loadingMore,
	loadMoreError,
	currentPage,
	loadMoreTriggerRowIndex,
	onRetryInitial,
	onRetryLoadMore,
	onOfferClick,
	onLoadMoreTriggerRow,
}) {
	if (initialError && offers.length === 0) {
		return <EmptyState message="Could not fetch offers" retry={onRetryInitial} />;
	}
	if (!initialLoading && !initialError && offers.length === 0) {
		return <EmptyState message="No offers found" />;
	}
	if (offers.length === 0) {
		return null;
	}
	return (
		<>
			<Table>
				<thead>
					<Trow head top="0">
						<Thead width="22%">ID</Thead>
						<Thead width="24%">Title</Thead>
						<Thead width="16%">Code</Thead>
						<Thead width="14%">Type</Thead>
						<Thead width="14%">Sub-Type</Thead>
						<Thead width="10%">Status</Thead>
					</Trow>
				</thead>
				<Tbody>
					{offers.map((o, index) => (
						<Trow
							clickable
							key={o.id}
							id={o.id}
							innerRef={(node) => {
								if (index === loadMoreTriggerRowIndex) onLoadMoreTriggerRow(node);
							}}
							onClick={() => onOfferClick(o.id)}>
							<Tcol width="22%">{o.id || "-"}</Tcol>
							<Tcol width="24%">{o.title || "-"}</Tcol>
							<Tcol width="16%">{o.code || "-"}</Tcol>
							<Tcol width="14%">{o.offerType || "-"}</Tcol>
							<Tcol width="14%">{o.offerSubType || "-"}</Tcol>
							<Tcol width="10%">
								<OfferStatusBadge status={o.status} />
							</Tcol>
						</Trow>
					))}
				</Tbody>
			</Table>
			{renderLoadMoreFooter({ loadingMore, loadMoreError, currentPage, onRetryLoadMore })}
		</>
	);
}

const OFFERS_ROUTES = AppRoutes[DashboardModules.OFFERS];

const OfferList = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { query, restaurantId } = readFiltersFromSearchParams(searchParams);

	const [searchInput, setSearchInput] = useState(query);
	const listAnchorRef = useRef(null);

	const {
		offers,
		currentPage,
		hasNextPage,
		lastClickedOfferId,
		setLastClickedOfferId,
		initialLoading,
		filterTransitioning,
		loadingMore,
		initialError,
		loadMoreError,
		fetchMore,
		retryInitial,
		retryLoadMore,
	} = useOffersList({ query, restaurantId, listAnchorRef });

	const [loadMoreTriggerRow, setLoadMoreTriggerRow] = useState();
	const loadMoreTriggerRowIndex = Math.max(offers.length - OFFERS_LOAD_MORE_TRIGGER_OFFSET, 0);

	const searchParamsRef = useRef(searchParams);
	searchParamsRef.current = searchParams;

	const replaceFilters = useCallback(
		(next) => {
			const normalized = normalizeOffersFilters(next);
			setSearchParams(writeFiltersToSearchParams(searchParamsRef.current, normalized), {
				replace: true,
			});
		},
		[setSearchParams]
	);

	// Keep the input in sync when the URL changes (back/forward).
	useEffect(() => {
		setSearchInput(query);
	}, [query]);

	// Debounced search → URL. Clearing the box updates the URL immediately.
	useEffect(() => {
		const { query: nextQuery } = normalizeOffersFilters({ query: searchInput });
		if (nextQuery === query) {
			return;
		}

		if (!nextQuery) {
			replaceFilters({ query: "", restaurantId });
			return;
		}

		const timerId = setTimeout(() => {
			replaceFilters({ query: nextQuery, restaurantId });
		}, OFFERS_SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timerId);
	}, [searchInput, query, restaurantId, replaceFilters]);

	const onSearchInputChange = (value) => {
		setSearchInput(value);
	};

	const onRestaurantChange = (nextRestaurantId) => {
		replaceFilters({ query: searchInput, restaurantId: nextRestaurantId || "" });
	};

	useEffect(() => {
		if (!loadMoreTriggerRow || !listAnchorRef.current) return undefined;
		const observer = new IntersectionObserver(
			(entries) => {
				if (
					entries[0].isIntersecting &&
					hasNextPage &&
					!loadingMore &&
					!loadMoreError
				) {
					fetchMore(currentPage + 1);
				}
			},
			{ root: listAnchorRef.current, threshold: 1 }
		);
		observer.observe(loadMoreTriggerRow);
		return () => observer.disconnect();
	}, [loadMoreTriggerRow, hasNextPage, loadingMore, loadMoreError, currentPage, fetchMore]);

	useEffect(() => {
		if (!lastClickedOfferId || offers.length === 0 || initialLoading) {
			return;
		}
		const raf = requestAnimationFrame(() => {
			scrollToRequiredItem(lastClickedOfferId);
		});
		return () => cancelAnimationFrame(raf);
	}, [lastClickedOfferId, offers.length, initialLoading]);

	const goToCreate = () => navigate(OFFERS_ROUTES.CREATE.getPath());
	const goToDetails = (id) => {
		setLastClickedOfferId(id);
		navigate(OFFERS_ROUTES.DETAILS.getPath(id));
	};

	const pageLoading = initialLoading || filterTransitioning;

	return (
		<Page>
			<OffersFilterBar
				searchInput={searchInput}
				onSearchInputChange={onSearchInputChange}
				restaurantId={restaurantId}
				onRestaurantChange={onRestaurantChange}
				headerActions={
					<Button onClick={goToCreate} active>
						Create Offer
					</Button>
				}
			/>
			<ListRegion aria-busy={pageLoading}>
				{pageLoading && (
					<LoadingOverlay>
						<Loader />
					</LoadingOverlay>
				)}
				<ListScroll ref={listAnchorRef}>
					{renderListContent({
						initialLoading,
						initialError,
						offers,
						loadingMore,
						loadMoreError,
						currentPage,
						loadMoreTriggerRowIndex,
						onRetryInitial: retryInitial,
						onRetryLoadMore: retryLoadMore,
						onOfferClick: goToDetails,
						onLoadMoreTriggerRow: setLoadMoreTriggerRow,
					})}
				</ListScroll>
			</ListRegion>
		</Page>
	);
};

export default OfferList;
